import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Scan, Users, Copy, Trash2, RefreshCw, CheckCircle2, XCircle, Loader2, RotateCw } from 'lucide-react';

const LibraryManager = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanTask, setScanTask] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [people, setPeople] = useState([]);
  const [unknownFaces, setUnknownFaces] = useState([]);
  const [faceClusters, setFaceClusters] = useState([]);
  const [faceLabels, setFaceLabels] = useState({});
  const [clusterLabels, setClusterLabels] = useState({});
  const [trainingMode, setTrainingMode] = useState(false);
  const [savingLabels, setSavingLabels] = useState(false);
  const [stats, setStats] = useState(null);
  const [rematching, setRematching] = useState(false);
  const [useClusterMode, setUseClusterMode] = useState(true);

  useEffect(() => {
    fetchDuplicates();
    fetchPeople();
    fetchStats();
    if (useClusterMode) {
      fetchFaceClusters();
    } else {
      fetchUnknownFaces();
    }
  }, [useClusterMode]);

  const fetchDuplicates = async () => {
    try {
      const response = await axios.get(`${API}/library/duplicates`);
      setDuplicates(response.data.duplicates || []);
    } catch (error) {
      console.error('Failed to fetch duplicates');
    }
  };

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${API}/library/people`);
      setPeople(response.data.people || []);
    } catch (error) {
      console.error('Failed to fetch people');
    }
  };

  const fetchUnknownFaces = async () => {
    try {
      const response = await axios.get(`${API}/library/faces/unknown?limit=50`);
      setUnknownFaces(response.data.unknown_faces || []);
      
      // Initialize face labels state
      const initialLabels = {};
      response.data.unknown_faces.forEach(face => {
        initialLabels[face.id] = '';
      });
      setFaceLabels(initialLabels);
    } catch (error) {
      console.error('Failed to fetch unknown faces');
    }
  };

  const fetchFaceClusters = async () => {
    try {
      const response = await axios.get(`${API}/library/faces/clusters?limit=100&similarity_threshold=0.5`);
      setFaceClusters(response.data.clusters || []);
      
      // Initialize cluster labels state
      const initialLabels = {};
      response.data.clusters.forEach(cluster => {
        initialLabels[cluster.cluster_id] = '';
      });
      setClusterLabels(initialLabels);
    } catch (error) {
      console.error('Failed to fetch face clusters');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/library/faces/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const startScan = async (scanType) => {
    try {
      setScanning(true);
      const response = await axios.post(`${API}/library/scan?scan_type=${scanType}`);
      setScanTask(response.data);
      toast.success('Library scan started!');
      
      // Poll for progress
      pollScanProgress(response.data.task_id);
    } catch (error) {
      toast.error('Failed to start scan');
      setScanning(false);
    }
  };

  const pollScanProgress = async (taskId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/library/scan/${taskId}`);
        const task = response.data;
        
        setScanProgress(task.progress || 0);
        
        if (task.status === 'completed') {
          clearInterval(interval);
          setScanning(false);
          toast.success('Library scan completed!');
          fetchDuplicates();
          fetchPeople();
          fetchStats();
          if (useClusterMode) {
            fetchFaceClusters();
          } else {
            fetchUnknownFaces();
          }
        } else if (task.status === 'failed') {
          clearInterval(interval);
          setScanning(false);
          toast.error('Library scan failed');
        }
      } catch (error) {
        clearInterval(interval);
        setScanning(false);
      }
    }, 2000);
  };

  const handleDeleteDuplicateGroup = async (groupId, keepPhotoId) => {
    if (!window.confirm('Delete all duplicate photos in this group except the selected one?')) {
      return;
    }

    try {
      await axios.delete(`${API}/photos/duplicate-group/${groupId}?keep_photo_id=${keepPhotoId}`);
      toast.success('Duplicate photos deleted');
      fetchDuplicates();
    } catch (error) {
      toast.error('Failed to delete duplicates');
    }
  };

  const handleSaveTraining = async () => {
    let face_labels = [];
    
    if (useClusterMode) {
      // Handle cluster-based labeling
      Object.entries(clusterLabels).forEach(([clusterId, name]) => {
        if (name && name.trim() !== '') {
          const cluster = faceClusters.find(c => c.cluster_id === clusterId);
          if (cluster) {
            // Label all faces in the cluster
            cluster.faces.forEach(face => {
              face_labels.push({
                face_id: face.id,
                person_name: name.trim()
              });
            });
          }
        }
      });
    } else {
      // Handle individual face labeling
      face_labels = Object.entries(faceLabels)
        .filter(([faceId, name]) => name && name.trim() !== '')
        .map(([faceId, name]) => ({
          face_id: faceId,
          person_name: name.trim()
        }));
    }

    if (face_labels.length === 0) {
      toast.error('Please label at least one face');
      return;
    }

    setSavingLabels(true);
    try {
      const response = await axios.post(`${API}/library/faces/train`, { face_labels });
      toast.success(`Successfully trained ${response.data.labeled_count} faces!`);
      
      // Refresh data
      await fetchStats();
      if (useClusterMode) {
        await fetchFaceClusters();
      } else {
        await fetchUnknownFaces();
      }
      await fetchPeople();
      setTrainingMode(false);
    } catch (error) {
      toast.error('Failed to save training data');
    } finally {
      setSavingLabels(false);
    }
  };

  const handleLabelChange = (faceId, name) => {
    setFaceLabels(prev => ({
      ...prev,
      [faceId]: name
    }));
  };

  const handleClusterLabelChange = (clusterId, name) => {
    setClusterLabels(prev => ({
      ...prev,
      [clusterId]: name
    }));
  };

  const handleRematchFaces = async () => {
    setRematching(true);
    try {
      await axios.post(`${API}/library/faces/rematch`);
      toast.success('Re-matching faces with new training data...');
      
      // Wait a bit then refresh
      setTimeout(async () => {
        await fetchStats();
        if (useClusterMode) {
          await fetchFaceClusters();
        } else {
          await fetchUnknownFaces();
        }
        await fetchPeople();
        setRematching(false);
        toast.success('Face re-matching complete!');
      }, 3000);
    } catch (error) {
      toast.error('Failed to rematch faces');
      setRematching(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Library Manager</h1>
                <p className="text-sm text-gray-600">Organize, deduplicate, and tag your photo library</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Recognition Progress Stats */}
        {stats && stats.total_faces > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-6">
            <h2 className="text-lg font-bold mb-4">Recognition Progress</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stats.total_faces}</p>
                <p className="text-xs text-gray-600">Total Faces</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats.labeled_faces}</p>
                <p className="text-xs text-gray-600">Recognized</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{stats.unlabeled_faces}</p>
                <p className="text-xs text-gray-600">Unknown</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{stats.unique_people}</p>
                <p className="text-xs text-gray-600">People</p>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-lg">
                <p className="text-2xl font-bold text-indigo-600">{stats.recognition_percentage}%</p>
                <p className="text-xs text-gray-600">Complete</p>
              </div>
            </div>
            <Progress value={stats.recognition_percentage} className="h-2" />
          </div>
        )}

        {/* Training Section - Show if unknown faces exist */}
        {((useClusterMode && faceClusters.length > 0) || (!useClusterMode && unknownFaces.length > 0)) && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border-2 border-purple-200 mb-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Train Library Manager
                </h2>
                <p className="text-sm text-purple-700 mt-1">
                  {useClusterMode 
                    ? `Help AI identify people by naming ${faceClusters.length} face groups`
                    : `Help AI identify people by labeling ${unknownFaces.length} unknown faces`
                  }
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleRematchFaces}
                  disabled={rematching}
                  variant="outline"
                  size="sm"
                  className="border-purple-300"
                >
                  {rematching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Re-matching...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Re-match Faces
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => setTrainingMode(!trainingMode)}
                  variant={trainingMode ? "outline" : "default"}
                  className={trainingMode ? "" : "bg-purple-600 hover:bg-purple-700"}
                >
                  {trainingMode ? 'Cancel Training' : 'Start Training'}
                </Button>
              </div>
            </div>

            {trainingMode && (
              <div className="space-y-4 mt-6">
                <div className="bg-white rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-700">
                      {useClusterMode 
                        ? 'Name each person. All similar faces will be labeled at once.'
                        : 'Enter names for individual faces you recognize.'
                      }
                    </p>
                    <Button
                      onClick={() => setUseClusterMode(!useClusterMode)}
                      variant="outline"
                      size="sm"
                    >
                      {useClusterMode ? 'Switch to Individual Mode' : 'Switch to Cluster Mode'}
                    </Button>
                  </div>
                  
                  {useClusterMode ? (
                    <div className="space-y-4 max-h-[600px] overflow-y-auto">
                      {faceClusters.slice(0, 10).map((cluster) => (
                        <div key={cluster.cluster_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <img
                                src={`${API}/photos/${cluster.faces[0].photo_id}/image?thumbnail=true`}
                                alt="Representative face"
                                className="w-24 h-24 object-cover rounded-lg"
                              />
                              <div className="mt-2 text-center">
                                <span className="inline-block bg-purple-600 text-white text-xs px-2 py-1 rounded">
                                  {cluster.face_count} {cluster.face_count === 1 ? 'photo' : 'photos'}
                                </span>
                              </div>
                            </div>
                            <div className="flex-grow">
                              <Label className="text-sm font-semibold mb-2 block">
                                Who is this person?
                              </Label>
                              <Input
                                type="text"
                                placeholder="Enter name..."
                                value={clusterLabels[cluster.cluster_id] || ''}
                                onChange={(e) => handleClusterLabelChange(cluster.cluster_id, e.target.value)}
                                className="mb-2"
                              />
                              <p className="text-xs text-gray-500">
                                This person appears in {cluster.face_count} photos
                              </p>
                              {cluster.face_count > 1 && (
                                <div className="mt-2 flex gap-1 flex-wrap">
                                  {cluster.faces.slice(1, 5).map((face, idx) => (
                                    <img
                                      key={face.id}
                                      src={`${API}/photos/${face.photo_id}/image?thumbnail=true`}
                                      alt={`Similar face ${idx + 1}`}
                                      className="w-12 h-12 object-cover rounded border border-gray-300"
                                    />
                                  ))}
                                  {cluster.face_count > 5 && (
                                    <div className="w-12 h-12 bg-gray-200 rounded border border-gray-300 flex items-center justify-center text-xs text-gray-600">
                                      +{cluster.face_count - 5}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {faceClusters.length > 10 && (
                        <p className="text-sm text-gray-600 text-center">
                          Showing top 10 groups. More will appear after training.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
                      {unknownFaces.slice(0, 20).map((face) => (
                        <div key={face.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="relative mb-3">
                            <img
                              src={`${API}/photos/${face.photo_id}/image?thumbnail=true`}
                              alt={face.photo_filename}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          </div>
                          <Input
                            type="text"
                            placeholder="Enter name..."
                            value={faceLabels[face.id] || ''}
                            onChange={(e) => handleLabelChange(face.id, e.target.value)}
                            className="text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-2 truncate">
                            {face.photo_filename}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setTrainingMode(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveTraining}
                    disabled={savingLabels}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {savingLabels ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Training Data'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {!trainingMode && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Recommended:</strong> Train the AI with known faces, then use "Re-match Faces" to apply training to similar faces automatically.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Scan Controls */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-8">
          <h2 className="text-xl font-bold mb-4">Library Scanning</h2>
          
          {scanning ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <span className="text-gray-700">Scanning your library...</span>
              </div>
              <Progress value={scanProgress} className="h-2" />
              <p className="text-sm text-gray-600">{scanProgress}% complete</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                onClick={() => startScan('full')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white h-auto py-4 flex flex-col items-center gap-2"
              >
                <Scan className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-semibold">Full Scan</p>
                  <p className="text-xs opacity-90">Rotate, faces & duplicates</p>
                </div>
              </Button>
              
              <Button
                onClick={() => startScan('rotation')}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 border-2 border-orange-300 hover:bg-orange-50"
              >
                <RotateCw className="w-6 h-6 text-orange-600" />
                <div className="text-center">
                  <p className="font-semibold">Fix Orientation</p>
                  <p className="text-xs text-gray-600">Auto-rotate images</p>
                </div>
              </Button>
              
              <Button
                onClick={() => startScan('faces')}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Users className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-semibold">Detect Faces</p>
                  <p className="text-xs text-gray-600">Find people in photos</p>
                </div>
              </Button>
              
              <Button
                onClick={() => startScan('duplicates')}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Copy className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-semibold">Find Duplicates</p>
                  <p className="text-xs text-gray-600">Detect similar photos</p>
                </div>
              </Button>
            </div>
          )}
        </div>

        {/* Detected People */}
        {people.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Detected People
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {people.map((person) => (
                <div key={person.name} className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-2">
                    {person.name.charAt(0).toUpperCase()}
                  </div>
                  <p className="font-semibold text-sm">{person.name}</p>
                  <p className="text-xs text-gray-600">{person.photo_count} photos</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Duplicates */}
        {duplicates.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Duplicate Photos ({duplicates.length} groups)
            </h2>
            <div className="space-y-6">
              {duplicates.map((group) => (
                <DuplicateGroup
                  key={group.group_id}
                  group={group}
                  onDelete={handleDeleteDuplicateGroup}
                />
              ))}
            </div>
          </div>
        )}

        {duplicates.length === 0 && people.length === 0 && !scanning && (
          <div className="text-center py-20">
            <Scan className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No scan results yet</h3>
            <p className="text-gray-500 mb-6">Start a library scan to detect faces and find duplicates</p>
          </div>
        )}
      </main>
    </div>
  );
};

const DuplicateGroup = ({ group, onDelete }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(group.photos[0]?.id);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">{group.count} similar photos</h3>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onDelete(group.group_id, selectedPhoto)}
          disabled={!selectedPhoto}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Keep Selected, Delete Others
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {group.photos.map((photo) => (
          <div
            key={photo.id}
            className={`relative cursor-pointer rounded-lg overflow-hidden border-4 transition-all ${
              selectedPhoto === photo.id
                ? 'border-green-500 shadow-lg'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedPhoto(photo.id)}
          >
            <img
              src={`${API}/photos/${photo.id}/image?thumbnail=true`}
              alt={photo.caption || photo.filename}
              className="w-full h-32 object-cover"
            />
            {selectedPhoto === photo.id && (
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                <CheckCircle2 className="w-4 h-4" />
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2">
              <p className="text-xs truncate">{photo.filename}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LibraryManager;
